/** Defaults match anime.js scrambleText (v4.4+). */
export const SCRAMBLE_DEFAULT_CHARS = "a-zA-Z0-9!%#_";
export const SCRAMBLE_REVEAL_RATE = 60;
export const SCRAMBLE_SETTLE_DURATION_MS = 300;
export const SCRAMBLE_SETTLE_RATE = 30;

const CHAR_PRESETS: Record<string, string> = {
  lowercase: "a-z",
  uppercase: "A-Z",
  numbers: "0-9",
  symbols: "!%#_|*+=",
};

export type CreateScrambleTextOptions = {
  text: string;
  chars?: string;
  revealRate?: number;
  settleDuration?: number;
  settleRate?: number;
  duration?: number;
  random?: () => number;
};

export type ScrambleTextPlayback = {
  durationMs: number;
  frame: (progress: number) => string;
};

function clamp01(value: number): number {
  if (Number.isNaN(value)) {
    return 0;
  }
  return Math.min(1, Math.max(0, value));
}

export function expandCharRanges(input: string): string {
  let result = "";
  for (let index = 0; index < input.length; index += 1) {
    if (
      index + 2 < input.length &&
      input[index + 1] === "-" &&
      (input.charCodeAt(index) ?? 0) < (input.charCodeAt(index + 2) ?? 0)
    ) {
      const start = input.charCodeAt(index) ?? 0;
      const end = input.charCodeAt(index + 2) ?? 0;
      for (let code = start; code <= end; code += 1) {
        result += String.fromCharCode(code);
      }
      index += 2;
    } else {
      result += input[index] ?? "";
    }
  }
  return result;
}

function pickChar(chars: string, random: () => number): string {
  if (chars.length === 0) {
    return "";
  }
  const index = Math.floor(random() * chars.length);
  return chars[Math.min(chars.length - 1, Math.max(0, index))] ?? "";
}

export function scrambleDurationMs(
  length: number,
  revealRate = SCRAMBLE_REVEAL_RATE,
  settleDuration = SCRAMBLE_SETTLE_DURATION_MS
): number {
  if (length <= 0) {
    return settleDuration;
  }
  return (length - 1) * (1000 / revealRate) + settleDuration;
}

export function createScrambleText({
  text,
  chars: charsParam = SCRAMBLE_DEFAULT_CHARS,
  revealRate = SCRAMBLE_REVEAL_RATE,
  settleDuration = SCRAMBLE_SETTLE_DURATION_MS,
  settleRate = SCRAMBLE_SETTLE_RATE,
  duration,
  random = Math.random,
}: CreateScrambleTextOptions): ScrambleTextPlayback {
  const resolvedChars = expandCharRanges(CHAR_PRESETS[charsParam] ?? charsParam);
  const durationMs =
    duration && duration > 0 ? duration : scrambleDurationMs(text.length, revealRate, settleDuration);
  const settleRatio = durationMs > 0 ? Math.min(1, settleDuration / durationMs) : 1;
  const settleSpacing = text.length > 0 && settleRatio < 1 ? (1 - settleRatio) / text.length : 0;
  const stepRatio = durationMs > 0 && settleRate > 0 ? 1000 / (settleRate * durationMs) : 1;

  const cache = Array.from({ length: text.length }, () => pickChar(resolvedChars, random));
  let lastStep = -1;

  const frame = (progress: number): string => {
    const t = clamp01(progress);
    if (t >= 1) {
      return text;
    }
    if (text.length === 0) {
      return "";
    }

    const currentStep = Math.floor(t / stepRatio);
    const refresh = currentStep !== lastStep;
    if (refresh) {
      lastStep = currentStep;
    }

    let result = "";
    for (let index = 0; index < text.length; index += 1) {
      const glyph = text[index] ?? "";
      if (glyph === " ") {
        result += " ";
        continue;
      }
      if (t >= index * settleSpacing + settleRatio) {
        result += glyph;
        continue;
      }
      if (refresh) {
        cache[index] = pickChar(resolvedChars, random);
      }
      result += cache[index] ?? "";
    }
    return result;
  };

  return { durationMs, frame };
}
